import { Clock, Group, PerspectiveCamera, Scene } from 'three';

import type { SceneManager } from '../scene-manager.class';
import { getQualitySettings } from '../../../../hooks/use-three-kitchen/quality';
import { createRendererCfn } from './create-renderer.cfn';

/**
 * Initialize the scene manager
 * Creates renderer, scene, camera, clock, and sets up resize handling
 */
export const initializeCfn = (self: SceneManager, _params: Record<string, never>): void => {
	if (self.isActive) {
		return;
	}

	const config = self.config;
	const { container, quality = 7, xrEnabled = true } = config;

	// Get quality settings
	const qualitySettings = getQualitySettings(quality);
	self.setQualitySettings(qualitySettings);

	// Create renderer
	const renderer = createRendererCfn(self, {
		qualitySettings,
		xrEnabled,
		gpuPreference: config.gpuPreference ?? 'auto',
		antialiasOverride: config.antialias,
		pixelRatioOverride: config.pixelRatio
	});
	self.setRenderer(renderer);

	// Append renderer to container
	if (!container.contains(renderer.domElement)) {
		container.appendChild(renderer.domElement);
		renderer.setSize(container.clientWidth, container.clientHeight);
	}

	// Create scene
	const scene = new Scene();
	scene.background = self.backgroundColor;
	self.setScene(scene);

	// Create clock
	self.setClock(new Clock());

	// Create camera (match Haxe: FOV 40)
	// Near plane is quality-dependent for better depth buffer precision at high quality
	const camera = new PerspectiveCamera(
		40,
		container.clientWidth / container.clientHeight,
		qualitySettings?.cameraNear ?? 0.1,
		600
	);
	// Default camera position (y=104 matches calculated camera source height)
	camera.position.set(0, 104, 200);
	self.setCamera(camera);

	// Create object holder group
	const objectHolder = new Group();
	objectHolder.userData = { type: 'objectHolder' };
	scene.add(objectHolder);
	self.setObjectHolder(objectHolder);

	// Setup debounced resize handler — coalesces resize storms (drag, orientation, sidebar
	// open/close) into at most `resizeDebounceHz` setSize calls per second. Skips no-op
	// resizes when integer dimensions are unchanged.
	const debounceMs = 1000 / (config.resizeDebounceHz ?? 10);
	const settleMs = config.resizeSettleMs ?? 300;
	// Seed with current container dimensions so the ResizeObserver's initial
	// fire (which reports the starting size) isn't treated as a change.
	let lastW = Math.floor(container.clientWidth);
	let lastH = Math.floor(container.clientHeight);
	let sizeDirty = false;
	const resizeHandler = (): void => {
		// Trailing-edge settle timer — fires once the resize burst stops, and
		// only if an actual size change was applied during the burst.
		if (self.resizeSettleTimer !== null) {
			clearTimeout(self.resizeSettleTimer);
		}
		const settleTimer = setTimeout(() => {
			self.setResizeSettleTimer(null);
			if (sizeDirty) {
				sizeDirty = false;
				config.onResizeSettled?.();
			}
		}, settleMs);
		self.setResizeSettleTimer(settleTimer);

		if (self.resizeDebounceTimer !== null) return;
		const timer = setTimeout(() => {
			self.setResizeDebounceTimer(null);
			const currentRenderer = self.renderer;
			const currentCamera = self.camera;
			if (!currentRenderer || !currentCamera) return;
			const w = Math.floor(container.clientWidth);
			const h = Math.floor(container.clientHeight);
			if (w === lastW && h === lastH) return;
			lastW = w;
			lastH = h;
			sizeDirty = true;
			currentRenderer.setSize(w, h);
			currentCamera.aspect = w / h;
			currentCamera.updateProjectionMatrix();
			// Request a re-render so the resized viewport is immediately painted
			self.requestRender();
		}, debounceMs);
		self.setResizeDebounceTimer(timer);
	};
	self.setResizeHandler(resizeHandler);
	window.addEventListener('resize', resizeHandler);

	// Observe container size changes directly — window 'resize' doesn't fire
	// when CSS layout shifts (e.g. dock/sidebar open/close, flex parent resize).
	if (typeof ResizeObserver !== 'undefined') {
		const observer = new ResizeObserver(resizeHandler);
		observer.observe(container);
		self.setResizeObserver(observer);
	}

	self.setIsActive(true);
};
